import { User } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface GoogleDirectoryUser {
  id: string;
  primaryEmail: string;
  name: {
    givenName: string;
    familyName: string;
    fullName: string;
  };
  thumbnailPhotoUrl?: string;
  orgUnitPath?: string;
  suspended?: boolean;
  addresses?: Array<{
    type?: string;
    formatted?: string;
    streetAddress?: string;
    locality?: string;
    region?: string;
    postalCode?: string;
    country?: string;
    primary?: boolean;
  }>;
  organizations?: Array<{
    name?: string;
    title?: string;
    department?: string;
    location?: string;
    type?: string;
    primary?: boolean;
  }>;
  relations?: Array<{
    type?: string;
    value?: string;
  }>;
  customSchemas?: any;
}

/**
 * Google Directory Service
 * Uses Firebase Functions with service account for server-side API calls.
 * No client-side OAuth tokens required.
 */
export class GoogleDirectoryService {
  private static instance: GoogleDirectoryService;
  private functions = getFunctions();

  static getInstance(): GoogleDirectoryService {
    if (!GoogleDirectoryService.instance) {
      GoogleDirectoryService.instance = new GoogleDirectoryService();
    }
    return GoogleDirectoryService.instance;
  }

  /**
   * Get all organization users from Google Directory via Firebase Functions
   * Uses service account with domain-wide delegation - no client OAuth needed
   */
  async getOrganizationUsers(_firebaseUser: User): Promise<GoogleDirectoryUser[]> {
    try {
      console.log('🔍 Getting organization users via Firebase Functions...');
      
      const getOrganizationUsersFunc = httpsCallable(this.functions, 'getOrganizationUsers');
      const response = await getOrganizationUsersFunc({ 
        domain: 'bvt.co.nz', 
        maxResults: 500 
      });
      
      const result = response.data as { success: boolean; data?: GoogleDirectoryUser[]; message?: string };
      
      if (!result.success || !result.data) {
        console.log('ℹ️ No users found in the organization');
        return [];
      }

      console.log(`✅ Found ${result.data.length} users in organization`);
      return result.data;

    } catch (error: any) {
      console.error('❌ Error fetching organization users:', error);
      
      // Handle Firebase Functions errors
      if (error.code === 'functions/permission-denied') {
        throw new Error('Insufficient permissions. Admin access required to view organization users.');
      } else if (error.code === 'functions/unauthenticated') {
        throw new Error('Authentication required. Please sign in to access team members.');
      } else {
        throw new Error(`Failed to load team members: ${error.message || 'Unknown error'}`);
      }
    }
  }

  /**
   * Search users in the organization
   * Note: For simplicity, this filters from the full user list client-side
   * A dedicated search function could be added to Firebase Functions if needed
   */
  async searchUsers(query: string, firebaseUser: User): Promise<GoogleDirectoryUser[]> {
    try {
      console.log(`🔍 Searching for users with query: "${query}"`);
      
      // Get all users and filter client-side
      // This is simpler than adding a new Firebase Function for search
      const allUsers = await this.getOrganizationUsers(firebaseUser);
      
      const searchLower = query.toLowerCase();
      const filteredUsers = allUsers.filter(user => 
        user.name.fullName.toLowerCase().includes(searchLower) ||
        user.primaryEmail.toLowerCase().includes(searchLower)
      );

      console.log(`✅ Found ${filteredUsers.length} users for query: "${query}"`);
      return filteredUsers;

    } catch (error: any) {
      console.error('❌ Error searching users:', error);
      throw new Error(`Failed to search users: ${error.message || 'Unknown error'}`);
    }
  }
} 